import _ from 'lodash';
import React, {FC, Fragment} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {RendererOf} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {TeamModel} from '../../../../libs/shared/core/src/models/api/teamModel';
import {isTeammateNamespace} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {TeamAvatar} from '../../../../libs/shared/ui/src/components/avatar/teamAvatar';
import {
  DropdownItem,
  DropdownItemProps,
} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipOverflowCondition} from '../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {VisuallyHidden} from '../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {TagModel} from '../../../models/api/tagModel';
import {getStarredTagIds} from '../../../store/data/tags/tagsSelectors';
import {ConversationHeaderTagPillTooltip} from '../../inboxes/viewer/conversation/header/tag/conversationHeaderTagPillTooltip';
import {DropdownItemTagIcon} from './dropdownItemTagIcon';

/*
 * Props.
 */

interface TagDropdownItemProps extends DropdownItemProps {
  tag: TagModel;
  nestingLevel: number;
  /** The tags path, starting with the leaf tag and then working up the nesting tree. */
  tagsPath: ReadonlyArray<TagModel>;
  shouldCollapseBreadcrumb?: boolean;
  team?: TeamModel;
  shouldShowDeduplication?: boolean;
  isEditingWorkspace?: boolean;
  tagNameRenderer?: RendererOf<TagModel>;
}

/*
 * Style.
 */

const StyledContentDiv = styled.div`
  display: flex;
  align-items: center;
  max-width: 100%;
`;

const StyledArchivedBadge = styled.div`
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.veryTiny};
  color: ${(p) => p.theme.greys.shade80};
  text-transform: uppercase;
  padding: 3px 5px 3px;
  background-color: ${(p) => p.theme.alphas.gray20};
  border-radius: 100px;
  display: inline-block;
  margin-right: 6px;
  line-height: 1;
  vertical-align: middle;
  margin-top: -1px;
`;

interface TagDropdownItemStyleProps {
  $isLastTag: boolean;
}
const StyledTagNameSpan = styled.span<TagDropdownItemStyleProps>`
  direction: ltr;
  color: ${(p) => (p.$isLastTag ? 'inherit' : p.theme.greys.shade60)};
  font-weight: ${(p) => (p.$isLastTag ? 'inherit' : fontWeights.normal)};
`;

const StyledChevronSpan = styled.span`
  display: inline-block;
  vertical-align: middle;
  margin: 0 3px;
`;

const StyledIconDiv = styled.div`
  flex-shrink: 0;
  margin-left: 3px;
`;

const StyledTagNameWrapperDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  overflow: hidden;
`;

/*
 * Component.
 */

export const TagDropdownItem: FC<TagDropdownItemProps> = (props) => {
  const {tag, tagsPath, team, shouldShowDeduplication, shouldCollapseBreadcrumb, tagNameRenderer} = props;
  const starredTagIds = useAppSelector(getStarredTagIds);
  const isStarredTag = starredTagIds.has(tag.id);

  return (
    <DropdownItem {...props} key={tag.id} renderTooltip={() => renderTagNameInTooltip(tag, tagsPath)}>
      <DropdownItemTagIcon tag={tag} nestingLevel={props.nestingLevel} />
      <DropdownItemContent
        isChecked={props.isChecked}
        isEditingWorkspace={props.isEditingWorkspace}
        className={props.className}
      >
        <StyledContentDiv>
          {renderFullTagName(tag, tagsPath || [], isStarredTag, shouldCollapseBreadcrumb, tagNameRenderer)}
          {maybeRenderDeduplicationIcon(tag, team, shouldShowDeduplication)}
        </StyledContentDiv>
      </DropdownItemContent>
    </DropdownItem>
  );
};

export function renderTagNameInTooltip(tag: TagModel, tagsPath?: ReadonlyArray<TagModel>) {
  const finalTagsPath = tagsPath && tagsPath.length > 1 ? tagsPath : [tag];
  return <ConversationHeaderTagPillTooltip tagsPath={finalTagsPath} />;
}

export function renderFullTagName(
  tag: TagModel,
  tagsPath: ReadonlyArray<TagModel>,
  isStarredTag: boolean,
  shouldCollapseBreadcrumb: boolean = false,
  tagNameRenderer?: RendererOf<TagModel>,
) {
  // If a tags path was provided, use it.
  if (!shouldCollapseBreadcrumb && tagsPath.length > 1) {
    return (
      <TooltipOverflowCondition isRightToLeft>
        {renderTagsPathName(tagsPath, tagNameRenderer)}
      </TooltipOverflowCondition>
    );
  }

  if (shouldCollapseBreadcrumb && tagsPath.length > 1) {
    return renderCollapsedBreadcrumbTagName(tag, tagNameRenderer);
  }

  // Otherwise, just render the single tag.
  return (
    <StyledTagNameWrapperDiv>
      {tag.archivedAt && (
        <StyledArchivedBadge>
          <Trans id="tag-archived">Archived</Trans>
        </StyledArchivedBadge>
      )}
      <TooltipOverflowCondition>{renderTagName(tag, isStarredTag, tagNameRenderer)}</TooltipOverflowCondition>
    </StyledTagNameWrapperDiv>
  );
}

function renderTagName(tag: TagModel, isStarredTag?: boolean, tagNameRenderer?: RendererOf<TagModel>) {
  if (isStarredTag) {
    return <Trans id="tags-starredtag">Star</Trans>;
  }

  // If this is the inbox tag, localize.
  if (tag.isMainInboxTag) {
    return <Trans id="tags-inboxtag">Inbox</Trans>;
  }

  if (tagNameRenderer) {
    return tagNameRenderer(tag);
  }

  // Otherwise, just use the name.
  return tag.name;
}

/**
 * There are a couple of tricks going on here, the effect we want to achieve is the following:
 *
 * ...oobar > Bar
 *
 * 1. We reverse the tag collection to be able to have them laid out in the right order without resorting to Flexbox based methods, which would make the whole effect near-impossible to achieve.
 * 2. We add `direction: ltr` to the names so punctuation doesn't get reversed.
 * 3. We place the ">" icon *after* the tag name, because our parent has a direction of right to left, so the (inline) elements will flow in that direction.
 *
 * @param tagsPath The tags path, starting with the leaf tag and then working up the nesting tree.
 */
export function renderTagsPathName(
  tagsPath: ReadonlyArray<TagModel>,
  tagNameRenderer?: RendererOf<TagModel>,
) {
  return _(tagsPath)
    .slice()
    .reverse()
    .map((tag, index, array) => {
      const isLast = index === array.length - 1;
      return (
        <UserThemeConsumer key={tag.id}>
          {(theme) => (
            <Fragment key={tag.id}>
              <StyledTagNameSpan $isLastTag={isLast}>
                {tag.archivedAt && (
                  <StyledArchivedBadge>
                    <Trans id="tag-archived">Archived</Trans>
                  </StyledArchivedBadge>
                )}
                {renderTagName(tag, undefined, tagNameRenderer)}
              </StyledTagNameSpan>
              {!isLast && (
                <StyledChevronSpan>
                  <Icon name="chevronRightTiny" color={theme.greys.shade60} />
                </StyledChevronSpan>
              )}
            </Fragment>
          )}
        </UserThemeConsumer>
      );
    })
    .value();
}

function renderCollapsedBreadcrumbTagName(tag: TagModel, tagNameRenderer: RendererOf<TagModel> | undefined) {
  // Same as renderTagsPathName, except the breadcrumb (of the tag ancestors) is always "overflowing" into an ellipsis.
  return (
    <TooltipOverflowCondition overrideCondition>
      <UserThemeConsumer>
        {(theme) => (
          <>
            <StyledTagNameSpan $isLastTag={false}>…</StyledTagNameSpan>
            <StyledChevronSpan>
              <Icon name="chevronRightTiny" color={theme.greys.shade60} />
            </StyledChevronSpan>
          </>
        )}
      </UserThemeConsumer>
      {tag.archivedAt && (
        <StyledArchivedBadge>
          <Trans id="tag-archived">Archived</Trans>
        </StyledArchivedBadge>
      )}
      {renderTagName(tag, undefined, tagNameRenderer)}
    </TooltipOverflowCondition>
  );
}

export function maybeRenderDeduplicationIcon(
  tag: TagModel,
  team?: TeamModel,
  shouldShowDeduplication?: boolean,
) {
  // If this is a private tag, show the lock icon.
  const isTeammateTag = isTeammateNamespace(tag.namespace);
  if (isTeammateTag) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <StyledIconDiv>
            <VisuallyHidden>
              <Trans id="tags-private">Private</Trans>
            </VisuallyHidden>
            <Icon name="lockFilledTiny" color={theme.greys.shade70} />
          </StyledIconDiv>
        )}
      </UserThemeConsumer>
    );
  }

  // Otherwise, if deduplication was requested, render the team avatar.
  if (shouldShowDeduplication && team) {
    return (
      <StyledIconDiv>
        <VisuallyHidden>{team.name}</VisuallyHidden>
        <TeamAvatar team={team} size={VisualSizesEnum.SMALL} />
      </StyledIconDiv>
    );
  }

  return null;
}
